import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Plus,
  Trash2,
  Wand2,
  Image as ImageIcon,
  Sparkles,
  RefreshCw,
} from "lucide-react";
import { Layout } from "@/components/hud/Layout";
import { Button } from "@/components/primitives/Button";
import { Card } from "@/components/primitives/Card";
import { Input } from "@/components/primitives/Input";
import { StatusPill } from "@/components/primitives/StatusPill";
import { SceneBoard } from "@/components/SceneBoard";
import { Montaggio } from "@/components/Montaggio";
import {
  getProject,
  listScenes,
  listReferences,
  createScene,
  deleteScene,
  storyToScenes,
  regenerateScenes,
  listKeyframes,
  createReference,
  deleteReference,
  updateScene,
  refImageUrl,
  deleteProject,
  generateKeyframe,
  animateScene,
  listGenerationJobs,
  getCostSummary,
  type Project,
  type Scene,
  type Reference,
  type ReferenceType,
  type GenerationJob,
  type CostSummary,
  type Keyframe,
} from "@/lib/api";
import { genStatusMeta } from "@/lib/status";
import { translateError } from "@/lib/errors";
import { PROJECT_DETAIL } from "@/constants/testIds";

// Format money: show 4 decimals for sub-cent amounts so real LLM cost is visible.
const money = (v?: number | null) => {
  if (v == null) return "—";
  return `$${v > 0 && v < 0.01 ? v.toFixed(4) : v.toFixed(2)}`;
};

const REF_TYPES: { value: ReferenceType; labelKey: string }[] = [
  { value: "character", labelKey: "projectDetail.refTypeCharacter" },
  { value: "environment", labelKey: "projectDetail.refTypeEnvironment" },
  { value: "style", labelKey: "projectDetail.refTypeStyle" },
];

export default function ProjectDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [project, setProject] = useState<Project | null>(null);
  const [scenes, setScenes] = useState<Scene[]>([]);
  const [references, setReferences] = useState<Reference[]>([]);
  const [jobs, setJobs] = useState<GenerationJob[]>([]);
  const [cost, setCost] = useState<CostSummary | null>(null);
  const [keyframes, setKeyframes] = useState<Record<string, Keyframe[]>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sceneTitle, setSceneTitle] = useState("");
  const [refForm, setRefForm] = useState<{ name: string; type: ReferenceType }>({
    name: "",
    type: "character",
  });
  const [refFiles, setRefFiles] = useState<File[]>([]);

  const load = async () => {
    setLoading(true);
    try {
      const [p, s, r, j, c] = await Promise.all([
        getProject(id),
        listScenes(id),
        listReferences(id),
        listGenerationJobs(id),
        getCostSummary(id),
      ]);
      setProject(p);
      setScenes(s);
      setReferences(r);
      setJobs(j);
      setCost(c);
      setLoading(false); // render core data immediately; keyframes load in background
      const kfEntries = await Promise.all(
        s.map((sc) =>
          listKeyframes(sc.id)
            .then((k) => [sc.id, k] as const)
            .catch(() => [sc.id, [] as Keyframe[]] as const),
        ),
      );
      setKeyframes(Object.fromEntries(kfEntries));
    } catch {
      toast.error(t("projectDetail.notFound"));
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const runStoryToScenes = async () => {
    setBusy(true);
    try {
      const created = await storyToScenes(id);
      toast.success(t("projectDetail.scenesGenerated", { count: created.length }));
      load();
    } catch (e: any) {
      toast.error(translateError(e?.response?.data?.detail, "projectDetail.decomposeFailed"));
    } finally {
      setBusy(false);
    }
  };

  const runRegenerateScenes = async () => {
    if (!window.confirm(t("projectDetail.regenerateConfirm"))) return;
    setBusy(true);
    try {
      const created = await regenerateScenes(id);
      toast.success(t("projectDetail.scenesRegenerated", { count: created.length }));
      load();
    } catch (e: any) {
      toast.error(translateError(e?.response?.data?.detail, "projectDetail.regenerateFailed"));
    } finally {
      setBusy(false);
    }
  };

  const addScene = async () => {
    if (!sceneTitle.trim()) return;
    try {
      await createScene(id, { title: sceneTitle.trim() });
      setSceneTitle("");
      toast.success(t("projectDetail.sceneAdded"));
      load();
    } catch {
      toast.error(t("projectDetail.sceneAddFailed"));
    }
  };

  const removeScene = async (sid: string) => {
    await deleteScene(sid);
    toast.success(t("projectDetail.sceneDeleted"));
    load();
  };

  const ALLOWED_REF_TYPES = ["image/jpeg", "image/png", "image/webp"];
  const MAX_REF_BYTES = 8 * 1024 * 1024;

  const handleRefFiles = (fileList: FileList | null) => {
    if (!fileList) return;
    const valid: File[] = [];
    for (const f of Array.from(fileList)) {
      if (!ALLOWED_REF_TYPES.includes(f.type)) {
        toast.error(t("projectDetail.refBadFormat", { name: f.name }));
        continue;
      }
      if (f.size > MAX_REF_BYTES) {
        toast.error(t("projectDetail.refTooLarge", { name: f.name }));
        continue;
      }
      valid.push(f);
    }
    setRefFiles(valid);
  };

  const addReference = async () => {
    if (!refForm.name.trim()) {
      toast.error(t("projectDetail.refNameRequired"));
      return;
    }
    if (refFiles.length === 0) {
      toast.error(t("projectDetail.refImageRequired"));
      return;
    }
    try {
      await createReference(id, { name: refForm.name.trim(), type: refForm.type }, refFiles);
      setRefForm({ name: "", type: "character" });
      setRefFiles([]);
      toast.success(t("projectDetail.refAdded"));
      load();
    } catch (e: any) {
      toast.error(translateError(e?.response?.data?.detail, "projectDetail.refAddFailed"));
    }
  };

  const toggleSceneRef = async (scene: Scene, refId: string) => {
    const current = scene.reference_ids || [];
    const next = current.includes(refId)
      ? current.filter((x) => x !== refId)
      : [...current, refId];
    try {
      await updateScene(scene.id, { reference_ids: next });
      load();
    } catch {
      toast.error(t("projectDetail.sceneRefsFailed"));
    }
  };

  const removeReference = async (rid: string) => {
    await deleteReference(rid);
    load();
  };

  const doGenerateKeyframe = async (sid: string) => {
    try {
      const kf = await generateKeyframe(sid);
      toast.success(t("projectDetail.keyframeQueued"));
      if (kf?.warning) toast.warning(kf.warning, { duration: 8000 });
      load();
    } catch (e: any) {
      toast.error(translateError(e?.response?.data?.detail, "projectDetail.keyframeFailed"));
    }
  };

  const doAnimate = async (sid: string) => {
    try {
      await animateScene(sid);
      toast.success(t("projectDetail.animateQueued"));
      load();
    } catch (e: any) {
      toast.error(translateError(e?.response?.data?.detail, "projectDetail.animateFailed"));
    }
  };

  const removeProject = async () => {
    if (!window.confirm(t("projectDetail.deleteConfirm"))) return;
    await deleteProject(id);
    toast.success(t("projectDetail.projectDeleted"));
    navigate("/dashboard");
  };

  if (loading) {
    return (
      <Layout>
        <main className="mx-auto max-w-5xl px-6 pt-24">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}
          </div>
        </main>
      </Layout>
    );
  }

  return (
    <Layout>
      <main data-testid={PROJECT_DETAIL.page} className="mx-auto max-w-5xl px-6 pb-24 pt-12">
        <Link
          to="/dashboard"
          data-testid={PROJECT_DETAIL.backBtn}
          className="mb-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground transition-colors hover:text-[#2E7BFF]"
        >
          <ArrowLeft className="h-3 w-3" /> {t("projectDetail.projects")}
        </Link>

        <div className="mb-10 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1
              data-testid={PROJECT_DETAIL.title}
              className="font-display text-4xl font-bold uppercase tracking-wide text-foreground"
            >
              {project?.title}
            </h1>
            {project?.logline && (
              <p className="mt-2 max-w-xl text-sm text-muted-foreground">
                {project.logline}
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            data-testid={PROJECT_DETAIL.deleteProjectBtn}
            onClick={removeProject}
            className="text-[#FF5C6C] hover:bg-[#FF5C6C]/10"
          >
            <Trash2 className="h-4 w-4" /> {t("common.delete")}
          </Button>
        </div>

        {/* Story */}
        {project?.story && (
          <Card className="mb-8">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("projectDetail.story")}
            </p>
            <p className="whitespace-pre-line text-sm leading-relaxed text-foreground/90">
              {project.story}
            </p>
          </Card>
        )}

        {/* Scenes */}
        <section className="mb-12">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-2xl font-semibold uppercase tracking-wide text-foreground">
              {t("projectDetail.scenes")}
            </h2>
            <div className="flex flex-wrap gap-2">
              {scenes.length > 0 && (
                <Button
                  data-testid="project-regenerate-scenes-btn"
                  onClick={runRegenerateScenes}
                  disabled={busy || !project?.story}
                  variant="ghost"
                >
                  <RefreshCw className="h-4 w-4" /> {t("projectDetail.regenerate")}
                </Button>
              )}
              <Button
                data-testid={PROJECT_DETAIL.storyToScenesBtn}
                onClick={runStoryToScenes}
                disabled={busy || !project?.story}
                variant="secondary"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {t("projectDetail.generateFromStory")}
              </Button>
            </div>
          </div>

          {/* Manual add */}
          <div className="mb-5 flex gap-2">
            <div className="flex-1">
              <Input
                name="scene-title"
                value={sceneTitle}
                onChange={(e) => setSceneTitle(e.target.value)}
                placeholder={t("projectDetail.addScenePlaceholder")}
                onKeyDown={(e) => e.key === "Enter" && addScene()}
              />
            </div>
            <Button variant="secondary" data-testid={PROJECT_DETAIL.addSceneBtn} onClick={addScene}>
              <Plus className="h-4 w-4" /> {t("common.add")}
            </Button>
          </div>

          {scenes.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("projectDetail.noScenes")}
            </p>
          ) : (
            <SceneBoard scenes={scenes} references={references} onChanged={load} />
          )}
        </section>

        {/* Montaggio — Il tuo video */}
        {scenes.length > 0 && (
          <Montaggio projectId={id} projectTitle={project?.title || ""} scenes={scenes} />
        )}

        {/* References */}
        <section>
          <h2 className="mb-4 font-display text-2xl font-semibold uppercase tracking-wide text-foreground">
            {t("projectDetail.references")}
          </h2>
          <div className="mb-5 flex flex-wrap gap-2">
            <div className="min-w-[200px] flex-1">
              <Input
                name="ref-name"
                value={refForm.name}
                onChange={(e) => setRefForm({ ...refForm, name: e.target.value })}
                placeholder={t("projectDetail.refNamePlaceholder")}
                onKeyDown={(e) => e.key === "Enter" && addReference()}
              />
            </div>
            <select
              value={refForm.type}
              onChange={(e) =>
                setRefForm({ ...refForm, type: e.target.value as ReferenceType })
              }
              className="h-10 field-premium !rounded-md px-3 text-sm focus:border-[#2E7BFF] focus:outline-none"
            >
              {REF_TYPES.map((rt) => (
                <option key={rt.value} value={rt.value}>
                  {t(rt.labelKey)}
                </option>
              ))}
            </select>
            <Button variant="secondary" data-testid={PROJECT_DETAIL.addReferenceBtn} onClick={addReference}>
              <Plus className="h-4 w-4" /> {t("common.add")}
            </Button>
          </div>

          <div className="mb-5">
            <label
              htmlFor="ref-file-input"
              className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-[#2E7BFF]/30 bg-[#05070D] px-4 py-3 text-sm text-muted-foreground transition-colors hover:border-[#2E7BFF]/60"
            >
              <ImageIcon className="h-4 w-4 text-[#2E7BFF]" />
              <span>
                {refFiles.length > 0
                  ? t("projectDetail.filesSelected", { count: refFiles.length })
                  : t("projectDetail.uploadImages")}
              </span>
            </label>
            <input
              id="ref-file-input"
              data-testid={PROJECT_DETAIL.referenceFileInput}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={(e) => handleRefFiles(e.target.files)}
            />
            <p className="mt-1.5 text-xs text-muted-foreground">
              {t("projectDetail.refHint")}
            </p>
          </div>

          {references.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("projectDetail.noReferences")}</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {references.map((r) => {
                const rt = REF_TYPES.find((x) => x.value === r.type);
                const label = rt ? t(rt.labelKey) : r.type;
                return (
                  <div
                    key={r.id}
                    data-testid={PROJECT_DETAIL.referenceItem}
                    className="rounded-2xl glass-panel p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-display text-sm font-semibold uppercase tracking-wide text-foreground">
                          {r.name}
                        </p>
                        <p className="text-xs uppercase tracking-wider text-[#2E7BFF]">{label}</p>
                      </div>
                      <button
                        onClick={() => removeReference(r.id)}
                        className="text-muted-foreground transition-colors hover:text-[#FF5C6C]"
                        aria-label={t("projectDetail.deleteReference")}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    {(r.images || []).length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(r.images || []).map((_, idx) => (
                          <img
                            key={idx}
                            data-testid={PROJECT_DETAIL.referenceImage}
                            src={refImageUrl(r.id, idx)}
                            alt={`${r.name} ${idx + 1}`}
                            className="h-16 w-16 rounded-md border border-[#2E7BFF]/30 object-cover"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Generation jobs & cost tracking */}
        <section className="mt-12">
          <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
            <h2 className="font-display text-2xl font-semibold uppercase tracking-wide text-foreground">
              {t("projectDetail.generationsCosts")}
            </h2>
            {cost && (
              <div
                data-testid={PROJECT_DETAIL.costSummary}
                className="rounded-md border border-[#2E7BFF]/30 bg-[#2E7BFF]/5 px-4 py-2 text-sm"
              >
                <span className="text-muted-foreground">{t("common.predictedCost")}: </span>
                <span className="font-display font-semibold text-[#2E7BFF]">
                  {money(cost.predicted_cost)}
                </span>
                {cost.effective_cost > 0 && (
                  <span className="ml-2 text-muted-foreground">
                    · {t("projectDetail.effective")}{" "}
                    <span className="font-display font-semibold text-[#3DDC84]">
                      {money(cost.effective_cost)}
                    </span>
                    {" ("}{cost.provider_reported_cost > 0 ? t("projectDetail.real") : t("projectDetail.calculated")}{")"}
                  </span>
                )}
                <span className="ml-2 text-xs text-muted-foreground">
                  ({t("projectDetail.jobs", { count: cost.job_count })})
                </span>
              </div>
            )}
          </div>

          {jobs.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("projectDetail.noGenerations")}
            </p>
          ) : (
            <div className="space-y-2">
              {jobs.map((j) => {
                const meta = genStatusMeta(j.status);
                return (
                  <div
                    key={j.id}
                    data-testid={PROJECT_DETAIL.jobItem}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-2xl glass-panel px-4 py-3"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-display text-sm font-semibold uppercase tracking-wide text-foreground">
                        {j.job_type.replace(/_/g, " ")}
                      </span>
                      <StatusPill tone={meta.tone} pulse={meta.pulse}>
                        {meta.label}
                      </StatusPill>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>
                        {t("projectDetail.predicted")} {money(j.cost.predicted_cost)}
                        {(j.cost.provider_reported_cost ?? j.cost.calculated_cost) != null && (
                          <>
                            {" · "}{j.cost.provider_reported_cost != null ? t("projectDetail.real") : t("projectDetail.calculated")}{" "}
                            <span className="text-[#3DDC84]">
                              {money(j.cost.provider_reported_cost ?? j.cost.calculated_cost)}
                            </span>
                          </>
                        )}
                      </span>
                      <span className="rounded border border-border px-2 py-0.5 uppercase tracking-wider text-[#7A8699]">
                        {j.cost.cost_source}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </Layout>
  );
}
